/* ============================================================
   LogOX Cart Rewards — single owner of all cart-drawer side effects:
   • auto add/remove free gifts when reward tiers are crossed
   • upsell "+" buttons ("Frequently bought together")
   • shipping-protection checkbox
   • discount-code field
   Config comes from the <script data-lx-cart-config> JSON rendered
   by snippets/cart-drawer.liquid (Theme settings → Cart Drawer — Rewards).

   Replaces the old duplicated gift logic that lived in cart.js
   (handleCartUpdate/updateCarts) and product-form.js (handleLogOXGiftLogic).
   ============================================================ */
(function () {
  'use strict';

  /* ===== custom code ITG -AP : CHECKOUT SAFETY — START ========
     (replaces the old `var syncing / var pending` flags and the old
      "checkout guard" block, both of which must NOT be kept)
     ============================================================
     CART ACTIVITY TRACKER + CHECKOUT SAFETY
     ------------------------------------------------------------
     Everything below exists so we always know whether the cart is settled.

     "Settled" means ALL of these are true:
       • no cart fetch/XHR is in flight (ours, Dawn's, or an app's),
       • the quantity-change debounce window has elapsed (Dawn waits
         ON_CHANGE_DEBOUNCE_TIMER ms before it even fires /cart/change),
       • a reconcile pass has confirmed the free-gift / protection lines in the
         cart match what the cart is entitled to right now.

     Checkout is held (and the whole drawer is covered by a loader) until the
     cart is settled, then the held click is replayed automatically. A hard
     timeout means a stuck request can never trap the customer on the drawer.
     ============================================================ */

  var RE_CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/i;
  var RE_CART_SECTIONS = /[?&](sections=[^&]*cart|section_id=cart)/i;
  var RE_CART_READ = /\/cart\.js(\?|$)/i;

  var CHANGE_DEBOUNCE_MS =
    (typeof ON_CHANGE_DEBOUNCE_TIMER !== 'undefined' ? ON_CHANGE_DEBOUNCE_TIMER : 300) + 250;
  var HOLD_MAX_MS = 12000; // hard cap: never trap the customer on a stuck request
  var STALE_MS = 30000; // re-verify if the last clean check is older than this
  var LOADER_DELAY_MS = 180; // don't flash the loader on very fast updates
  var LOADER_MIN_MS = 350; // ...and don't flash it off again immediately
  var LOADER_TEXT = 'Updating your cart…';
  var LOADER_TEXT_CHECKOUT = 'Redirecting to checkout…';

  var CHECKOUT_SELECTOR =
    '#CartDrawer-Checkout, #checkout, button[name="checkout"], input[name="checkout"]';

  var netOps = 0; // cart requests currently in flight (any source)
  var internalDepth = 0; // >0 while WE are issuing a cart request
  var syncRunning = false; // a reconcile pass is running
  var syncDirty = true; // cart may not match the gift/protection state it should have
  var syncFailures = 0;
  var pendingCartData = null;
  var debounceUntil = 0; // qty change accepted, Dawn's request not fired yet
  var lastVerifiedAt = 0;
  var idleWaiters = [];
  var checkoutHeld = false;
  var navigating = false;
  var releasing = false; // true while we replay the held checkout submit
  var ticker = null;
  var foreignTimer = null;

  var loaderVisible = false;
  var loaderShownAt = 0;
  var loaderShowTimer = null;
  var loaderHideTimer = null;

  function root() {
    return window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  }

  function isCartBusy() {
    return netOps > 0 || syncRunning || syncDirty || Date.now() < debounceUntil;
  }

  function isBlocking() {
    return isCartBusy() || checkoutHeld || navigating;
  }

  // self-stopping heartbeat: catches state that expires on a clock
  // (the debounce window) rather than on an event
  function kickTicker() {
    if (ticker) return;
    ticker = setInterval(function () {
      updateBusyUI();
      if (!isBlocking() && !loaderVisible) {
        clearInterval(ticker);
        ticker = null;
      }
    }, 120);
  }

  /* ---- request tracking ------------------------------------------ */

  function beginOp(markDirty) {
    netOps++;
    if (markDirty) {
      syncDirty = true;
      debounceUntil = 0; // the request we were waiting for has started
    }
    kickTicker();
    updateBusyUI();
  }

  function endOp(markDirty) {
    netOps = Math.max(0, netOps - 1);
    updateBusyUI();
    if (markDirty) scheduleForeignSync();
  }

  // Safety net for cart changes made by code that doesn't publish cartUpdate
  // (an app, a custom snippet). If nothing kicked a reconcile pass shortly
  // after the request finished, we kick one ourselves — otherwise syncDirty
  // would stay true forever and checkout would stay blocked.
  function scheduleForeignSync() {
    clearTimeout(foreignTimer);
    foreignTimer = setTimeout(function () {
      if (syncDirty && !syncRunning) requestSync(null);
    }, 350);
  }

  function classifyCartUrl(href) {
    if (!href) return null;
    if (RE_CART_MUTATION.test(href)) return { track: true, dirty: internalDepth === 0 };
    if (RE_CART_SECTIONS.test(href)) return { track: true, dirty: false };
    if (internalDepth > 0 && RE_CART_READ.test(href)) return { track: true, dirty: false };
    return null;
  }

  (function patchFetch() {
    var orig = window.fetch;
    if (typeof orig !== 'function') return;
    window.fetch = function () {
      var first = arguments[0];
      var href = typeof first === 'string' ? first : (first && first.url) || '';
      var kind = classifyCartUrl(String(href));
      if (!kind) return orig.apply(this, arguments);
      beginOp(kind.dirty);
      var promise;
      try {
        promise = orig.apply(this, arguments);
      } catch (e) {
        endOp(kind.dirty);
        throw e;
      }
      // observe only — the caller gets the original promise back untouched
      promise.then(
        function () {
          endOp(kind.dirty);
        },
        function () {
          endOp(kind.dirty);
        }
      );
      return promise;
    };
  })();

  (function patchXHR() {
    if (typeof XMLHttpRequest === 'undefined') return;
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        this.__lxCartKind = classifyCartUrl(String(url || ''));
      } catch (e) {}
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      var kind = xhr.__lxCartKind;
      if (kind) {
        var done = false;
        var finish = function () {
          if (done) return;
          done = true;
          endOp(kind.dirty);
        };
        beginOp(kind.dirty);
        xhr.addEventListener('loadend', finish);
        xhr.addEventListener('error', finish);
        xhr.addEventListener('abort', finish);
      }
      return send.apply(this, arguments);
    };
  })();

  // Dawn debounces the quantity `change` event before it calls /cart/change, so
  // for ~300ms after a stepper click there is no request in flight yet and the
  // cart looks idle. Treat that window as busy too, otherwise "change qty then
  // instantly click Checkout" slips straight through.
  function markPendingChange() {
    debounceUntil = Date.now() + CHANGE_DEBOUNCE_MS;
    kickTicker();
    updateBusyUI();
  }

  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      if (!target || !target.matches || !target.closest) return;
      if (!target.matches('.quantity__input, input[name="updates[]"]')) return;
      if (!target.closest('cart-drawer, cart-items, cart-drawer-items, #main-cart-items')) return;
      markPendingChange();
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (target && target.closest && target.closest('cart-remove-button')) markPendingChange();
    },
    true
  );

  /* ---- full-drawer loader ----------------------------------------- */
  // Lives as a direct child of <cart-drawer>, OUTSIDE #CartDrawer, so it
  // survives every render path (cart-drawer.js renderContents replaces
  // #CartDrawer's innerHTML; cart.js onCartUpdate and rerenderFromSections
  // replace .drawer__inner's innerHTML).

  function ensureLoader() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return null;
    var el = drawer.querySelector('[data-lx-loading]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'lx-loading';
      el.setAttribute('data-lx-loading', '');
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML =
        '<div class="lx-loading__panel" role="status" aria-live="polite">' +
        '<span class="lx-loading__spinner" aria-hidden="true"></span>' +
        '<span class="lx-loading__text"></span>' +
        '</div>';
      el.querySelector('.lx-loading__text').textContent = LOADER_TEXT;
      el.addEventListener('click', onLoaderClick);
      drawer.appendChild(el);
      if (loaderVisible) paintLoader(el, true);
    }
    return el;
  }

  function paintLoader(el, show) {
    if (!el) return;
    if (show) {
      el.removeAttribute('hidden');
      el.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(function () {
        el.classList.add('is-on');
      });
    } else {
      el.classList.remove('is-on');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('hidden', '');
    }
  }

  function setLoaderText(text) {
    var el = document.querySelector('[data-lx-loading] .lx-loading__text');
    if (el) el.textContent = text;
  }

  function syncCheckoutButtons() {
    var busy = loaderVisible || checkoutHeld || navigating;
    var buttons = document.querySelectorAll(CHECKOUT_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].classList.toggle('lx-btn-busy', busy);
      if (busy) buttons[i].setAttribute('aria-busy', 'true');
      else buttons[i].removeAttribute('aria-busy');
    }
  }

  function updateBusyUI() {
    if (isBlocking()) {
      if (loaderHideTimer) {
        clearTimeout(loaderHideTimer);
        loaderHideTimer = null;
      }
      if (!loaderVisible && !loaderShowTimer) {
        loaderShowTimer = setTimeout(function () {
          loaderShowTimer = null;
          if (!isBlocking()) return;
          loaderVisible = true;
          loaderShownAt = Date.now();
          paintLoader(ensureLoader(), true);
          syncCheckoutButtons();
        }, LOADER_DELAY_MS);
      }
      syncCheckoutButtons();
      return;
    }

    if (loaderShowTimer) {
      clearTimeout(loaderShowTimer);
      loaderShowTimer = null;
    }
    if (loaderVisible && !loaderHideTimer) {
      var wait = Math.max(0, LOADER_MIN_MS - (Date.now() - loaderShownAt));
      loaderHideTimer = setTimeout(function () {
        loaderHideTimer = null;
        if (isBlocking()) {
          updateBusyUI();
          return;
        }
        loaderVisible = false;
        paintLoader(ensureLoader(), false);
        syncCheckoutButtons();
      }, wait);
    }
    syncCheckoutButtons();
  }

  // shown straight away (no delay) when the customer clicks Checkout
  function forceLoader() {
    if (loaderShowTimer) {
      clearTimeout(loaderShowTimer);
      loaderShowTimer = null;
    }
    if (loaderHideTimer) {
      clearTimeout(loaderHideTimer);
      loaderHideTimer = null;
    }
    if (loaderVisible) return;
    loaderVisible = true;
    loaderShownAt = Date.now();
    paintLoader(ensureLoader(), true);
    syncCheckoutButtons();
  }

  /* ---- checkout gate ---------------------------------------------- */

  function waitForSettled(maxMs) {
    return new Promise(function (resolve) {
      var start = Date.now();
      (function tick() {
        if (!isCartBusy()) return resolve(true);
        if (Date.now() - start >= maxMs) return resolve(false);
        setTimeout(tick, 100);
      })();
    });
  }

  // a successful sync re-renders the drawer, which replaces the button node,
  // so the reference captured at click time may be detached by now
  function freshCheckoutButton(button) {
    if (button && button.id) {
      var byId = document.getElementById(button.id);
      if (byId) return byId;
    }
    if (button && button.isConnected) return button;
    return document.getElementById('CartDrawer-Checkout') || document.getElementById('checkout');
  }

  function submitCheckout(button) {
    var fresh = freshCheckoutButton(button);
    if (!fresh || fresh.disabled) return; // cart emptied while we waited
    var form = fresh.form;
    if (!form) return;

    navigating = true;
    setLoaderText(LOADER_TEXT_CHECKOUT);
    forceLoader();
    setTimeout(function () {
      // navigation never happened (blocked / cancelled) — release the drawer
      navigating = false;
      setLoaderText(LOADER_TEXT);
      updateBusyUI();
    }, 8000);

    releasing = true;
    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(fresh); // preserves name="checkout" on the submit
      } else {
        form.submit();
      }
    } finally {
      setTimeout(function () {
        releasing = false;
      }, 0);
    }
  }

  // returns true when the click was held (the caller must preventDefault)
  function guardCheckout(button) {
    // force a fresh verification if our last clean check has gone stale
    if (Date.now() - lastVerifiedAt > STALE_MS) syncDirty = true;
    if (!isCartBusy() && !checkoutHeld) return false; // settled — let it through
    if (checkoutHeld) return true; // already holding; swallow repeat clicks

    checkoutHeld = true;
    forceLoader();
    kickTicker();
    if (syncDirty && !syncRunning) pumpSync();
    updateBusyUI();

    waitForSettled(HOLD_MAX_MS).then(function (settled) {
      checkoutHeld = false;
      if (!settled) {
        console.warn('LogOX rewards: cart did not settle in time — continuing to checkout');
      }
      updateBusyUI();
      submitCheckout(button);
    });
    return true;
  }

  document.addEventListener(
    'click',
    function (event) {
      if (releasing) return;
      var target = event.target;
      if (!target || !target.closest) return;
      var button = target.closest(CHECKOUT_SELECTOR);
      if (!button || button.disabled) return;
      if (guardCheckout(button)) event.preventDefault();
    },
    true
  );

  // Enter inside the cart form submits it without a click
  document.addEventListener(
    'submit',
    function (event) {
      if (releasing) return;
      var form = event.target;
      if (!form || (form.id !== 'CartDrawer-Form' && form.id !== 'cart')) return;
      var submitter = event.submitter;
      if (submitter && submitter.name !== 'checkout') return;
      var button =
        submitter ||
        document.getElementById('CartDrawer-Checkout') ||
        document.getElementById('checkout');
      if (!button) return;
      if (guardCheckout(button)) event.preventDefault();
    },
    true
  );

  // The loader covers the checkout button, so a click aimed at it lands on the
  // overlay instead. Treat a click over the button's box as a checkout intent
  // and queue it rather than silently swallowing it.
  function onLoaderClick(event) {
    var button = document.getElementById('CartDrawer-Checkout');
    if (!button || button.disabled) return;
    var box = button.getBoundingClientRect();
    if (
      event.clientX < box.left ||
      event.clientX > box.right ||
      event.clientY < box.top ||
      event.clientY > box.bottom
    ) {
      return;
    }
    guardCheckout(button);
  }
  /* ===== custom code ITG -AP : CHECKOUT SAFETY — END ========== */

  function getConfig() {
    var el = document.querySelector('[data-lx-cart-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('LogOX rewards: bad config JSON', e);
      return null;
    }
  }

  function fetchCart() {
    internalDepth++;
    var request;
    try {
      request = fetch(root() + 'cart.js', { credentials: 'same-origin' });
    } finally {
      internalDepth--;
    }
    return request.then(function (res) {
      if (!res.ok) return null;
      return res.json();
    });
  }

  /* ---- rendering ------------------------------------------------ */

  function rerenderFromSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      var drawer = document.querySelector('cart-drawer');
      var inner = drawer && drawer.querySelector('.drawer__inner');
      var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var newInner = parsed.querySelector('.drawer__inner');
      if (drawer && inner && newInner) {
        inner.innerHTML = newInner.innerHTML;
        var newDrawer = parsed.querySelector('cart-drawer');
        if (newDrawer) drawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      }
    }
    if (sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        var parsedBubble = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        var src = parsedBubble.querySelector('.shopify-section');
        if (src) bubble.innerHTML = src.innerHTML;
      }
    }
  }

  function refreshDrawer() {
    return fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then(function (res) { return res.json(); })
      .then(rerenderFromSections)
      .catch(function (e) { console.error('LogOX rewards: drawer refresh failed', e); });
  }

  function openDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }

  // The page-load drawer HTML can be stale (Shopify edge cache) so a gift line
  // may render as a normal item (qty stepper + price) until the next AJAX cart
  // update replaces it with a fresh render. Detect that mismatch: a cart line
  // whose product is a configured gift but is NOT marked as a gift card.
  function giftMisclassified() {
    var cfg = getConfig();
    if (!cfg) return false;
    var giftPids = (cfg.giftProductIds || []).map(function (x) { return String(x).trim(); }).filter(Boolean);
    if (!giftPids.length) return false;
    var cards = document.querySelectorAll('cart-drawer .lx-card[data-product-id]');
    for (var i = 0; i < cards.length; i++) {
      var pid = cards[i].getAttribute('data-product-id');
      if (giftPids.indexOf(pid) !== -1 && !cards[i].classList.contains('lx-card--gift')) {
        return true;
      }
    }
    return false;
  }

  /* ---- "Frequently bought together" slider --------------------- */
  // Shows/hides the prev/next arrows based on overflow and updates their
  // disabled state. Re-run after every drawer render (see the observer below).
  function initUpsellSlider() {
    var track = document.querySelector('[data-lx-up-scroll]');
    var section = document.querySelector('[data-lx-up]');
    if (!track || !section) return;
    var max = track.scrollWidth - track.clientWidth;
    section.classList.toggle('is-scrollable', max > 4);
    var prev = section.querySelector('[data-lx-up-prev]');
    var next = section.querySelector('[data-lx-up-next]');
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  }

  /* ---- automatic Shopify recommendations ----------------------- */
  // Fetches related/complementary products for the cart's representative item
  // and renders them into the upsell track. Cached by product+intent+limit so
  // it only hits the network when the representative item changes.
  var recoCache = { key: null, cards: null };

  function loadAutoRecommendations() {
    var section = document.querySelector('[data-lx-upsell-auto]');
    if (!section) return; // manual mode (or upsells off)
    var track = section.querySelector('[data-lx-up-scroll]');
    if (!track) return;
    var rep = section.getAttribute('data-rep-product');
    if (!rep) {
      section.classList.add('lx-hide');
      if (track.innerHTML !== '') track.innerHTML = '';
      return;
    }
    var intent = section.getAttribute('data-intent') || 'related';
    var limit = section.getAttribute('data-limit') || '10';
    var key = rep + ':' + intent + ':' + limit;

    if (recoCache.key === key && recoCache.cards) {
      renderRecoCards(section, track, recoCache.cards);
      return;
    }
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    var url =
      root + 'recommendations/products?section_id=cart-recommendations&intent=' +
      encodeURIComponent(intent) + '&limit=' + encodeURIComponent(limit) +
      '&product_id=' + encodeURIComponent(rep);
    fetch(url)
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (html == null) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var cards = Array.prototype.map.call(doc.querySelectorAll('.lx-uc'), function (c) {
          return c.outerHTML;
        });
        recoCache = { key: key, cards: cards };
        renderRecoCards(section, track, cards);
      })
      .catch(function () {});
  }

  function renderRecoCards(section, track, cards) {
    var cfg = getConfig() || {};
    var giftPids = (cfg.giftProductIds || []).map(function (x) { return String(x).trim(); }).filter(Boolean);
    var protectPid = cfg.protectProductId != null ? String(cfg.protectProductId) : null;
    var inCart = {};
    document.querySelectorAll('cart-drawer .lx-card[data-product-id]').forEach(function (el) {
      inCart[el.getAttribute('data-product-id')] = true;
    });

    var tmp = document.createElement('div');
    tmp.innerHTML = cards.join('');
    Array.prototype.slice.call(tmp.querySelectorAll('.lx-uc')).forEach(function (card) {
      var pid = card.getAttribute('data-product-id');
      if (pid && (inCart[pid] || giftPids.indexOf(pid) !== -1 || (protectPid && pid === protectPid))) {
        card.parentNode.removeChild(card);
      }
    });

    var newHtml = tmp.innerHTML;
    // only touch the DOM when it actually changes — prevents a mutation-observer loop
    if (track.innerHTML !== newHtml) track.innerHTML = newHtml;
    section.classList.toggle('lx-hide', !track.querySelector('.lx-uc'));
    initUpsellSlider();
  }

  /* ---- gift sync ------------------------------------------------- */

  function desiredGiftQuantities(cart, cfg) {
    var items = cart.items || [];
    var giftPids = (cfg.giftProductIds || []).map(function (id) { return String(id).trim(); }).filter(Boolean);

    // Rewards require at least one LogOX item in the cart. Once one is present,
    // the WHOLE cart total (LogOX + non-LogOX, minus the protection line) counts
    // toward the gift thresholds — matches the progress bar in Liquid.
    var hasLogox = false;
    var rewardsTotal = 0;
    items.forEach(function (item) {
      var pid = String(item.product_id);
      if (cfg.protectProductId && pid === String(cfg.protectProductId)) return; // exclude protection fee
      rewardsTotal += item.final_line_price != null ? item.final_line_price : item.price * item.quantity;
      if (item.vendor === cfg.brandVendor && giftPids.indexOf(pid) === -1) hasLogox = true;
    });

    var wanted = {};
    (cfg.tier2Variants || []).forEach(function (id) {
      wanted[id] = hasLogox && rewardsTotal >= cfg.tier2Cents ? 1 : 0;
    });
    (cfg.tier3Variants || []).forEach(function (id) {
      wanted[id] = hasLogox && rewardsTotal >= cfg.tier3Cents ? 1 : 0;
    });
    return wanted;
  }

  /* ---- shipping protection tiers ------------------------------- */

  // cart value that drives the protection tier: everything EXCEPT the
  // protection line itself (avoids the fee bumping its own tier). gifts are
  // free so they add $0.
  function protectionSubtotalCents(cart, cfg) {
    var total = 0;
    (cart.items || []).forEach(function (item) {
      if (cfg.protectProductId && String(item.product_id) === String(cfg.protectProductId)) return;
      total += item.final_line_price != null ? item.final_line_price : item.price * item.quantity;
    });
    return total;
  }

  // largest tier whose minimum <= subtotal (falls back to the lowest tier)
  function matchTier(subtotalCents, cfg) {
    var tiers = cfg.protectTiers || [];
    if (!tiers.length) return null;
    var best = null;
    tiers.forEach(function (t) {
      if (subtotalCents >= t.minCents && (best === null || t.minCents > best.minCents)) best = t;
    });
    if (!best) {
      best = tiers.reduce(function (a, b) { return b.minCents < a.minCents ? b : a; });
    }
    return best;
  }

  // Only runs when protection is ALREADY in the cart (opt-in respected) — keeps
  // it on the tier that matches the current subtotal, swapping the variant when
  // the subtotal crosses a boundary.
  function desiredProtectionQuantities(cart, cfg) {
    var want = {};
    if (!cfg.protectEnabled || !cfg.protectProductId || !(cfg.protectTiers || []).length) return want;
    var items = cart.items || [];
    var inCart = items.filter(function (i) { return String(i.product_id) === String(cfg.protectProductId); });
    if (!inCart.length) return want;

    // count real paid products (exclude the protection line itself and free gifts)
    var giftPids = (cfg.giftProductIds || []).map(function (x) { return String(x).trim(); }).filter(Boolean);
    var realItems = items.filter(function (i) {
      var pid = String(i.product_id);
      if (pid === String(cfg.protectProductId)) return false;
      if (giftPids.indexOf(pid) !== -1) return false;
      return true;
    });

    var subtotal = protectionSubtotalCents(cart, cfg);

    // drop protection entirely if:
    //  • no paid products left (cart emptying), or
    //  • subtotal has reached the cutoff tier (top variant's threshold) —
    //    protection is not offered on high-value orders
    var overCutoff = cfg.protectCutoffCents > 0 && subtotal >= cfg.protectCutoffCents;
    if (!realItems.length || overCutoff) {
      inCart.forEach(function (i) { want[i.variant_id] = 0; });
      return want;
    }

    var tier = matchTier(subtotal, cfg);
    inCart.forEach(function (i) { want[i.variant_id] = 0; });
    if (tier) want[tier.variantId] = 1;
    return want;
  }

  function computeUpdates(cart, wanted) {
    var updates = {};
    var changed = false;
    Object.keys(wanted).forEach(function (variantId) {
      var line = (cart.items || []).find(function (item) {
        return String(item.variant_id) === String(variantId);
      });
      var current = line ? line.quantity : 0;
      if (current !== wanted[variantId]) {
        updates[variantId] = wanted[variantId];
        changed = true;
      }
    });
    return changed ? updates : null;
  }

  // `internal` marks a write issued by the reconcile pass itself: it still
  // counts as cart activity (so the loader stays up) but must not flag the cart
  // dirty again, or every gift add would trigger another reconcile pass.
  function applyUpdates(updates, internal) {
    if (internal) internalDepth++;
    var request;
    try {
      request = fetch(root() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: updates,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
    } finally {
      if (internal) internalDepth--;
    }
    return request.then(function (res) {
      if (res.status === 429) {
        console.warn('LogOX rewards: cart update rate-limited (429)');
        return null;
      }
      if (!res.ok) {
        console.warn('LogOX rewards: cart update failed', res.status);
        return null;
      }
      return res.json();
    });
  }

  /* ---- reconcile pump (custom code ITG -AP) ---------------------- */
  // One reconcile pass at a time, ever. A pass that is requested while another
  // is running just re-arms the dirty flag, so we never fire duplicate
  // add/remove calls for the same gift no matter how fast the customer clicks.

  // add.js responses also have `items`, but only the added lines — only a
  // change.js/update.js/cart.js payload can be trusted as the whole cart
  function isFullCart(data) {
    return !!(
      data &&
      data.token &&
      typeof data.item_count === 'number' &&
      Array.isArray(data.items)
    );
  }

  function whenIdle() {
    if (!syncRunning && !syncDirty) return Promise.resolve();
    return new Promise(function (resolve) {
      idleWaiters.push(resolve);
    });
  }

  function flushIdleWaiters() {
    if (!idleWaiters.length) return;
    var list = idleWaiters;
    idleWaiters = [];
    list.forEach(function (fn) {
      try {
        fn();
      } catch (e) {}
    });
  }

  function requestSync(cartData) {
    syncDirty = true;
    // a cart payload handed to us mid-pass is already stale by the time the
    // next pass starts — force that pass to re-read the cart instead
    pendingCartData = !syncRunning && isFullCart(cartData) ? cartData : null;
    kickTicker();
    updateBusyUI();
    if (!syncRunning) pumpSync();
  }

  // Applies the gift/protection updates and then re-checks the cart the server
  // actually returned, so "clean" means the server agrees — not just that we
  // sent a request. Bounded, so a gift Shopify refuses to add (sold out) can't
  // loop forever.
  function applyPass(cart, cfg, depth) {
    var wanted = {};
    if (cfg.autoAdd) Object.assign(wanted, desiredGiftQuantities(cart, cfg));
    Object.assign(wanted, desiredProtectionQuantities(cart, cfg));
    var updates = computeUpdates(cart, wanted);
    if (!updates) return Promise.resolve(true); // cart already matches
    if (depth >= 2) {
      console.warn('LogOX rewards: cart did not converge on the expected gift state');
      return Promise.resolve(true);
    }
    return applyUpdates(updates, true).then(function (data) {
      if (!data) return false; // request failed / rate-limited
      if (data.sections) rerenderFromSections(data.sections);
      return applyPass(data, cfg, depth + 1);
    });
  }

  function reconcile(cartData) {
    var cfg = getConfig();
    // nothing to reconcile if both features are off
    if (!cfg || (!cfg.autoAdd && !cfg.protectEnabled)) return Promise.resolve(true);
    var cartPromise = isFullCart(cartData) ? Promise.resolve(cartData) : fetchCart();
    return cartPromise.then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) return false;
      return applyPass(cart, cfg, 0);
    });
  }

  function pumpSync() {
    if (syncRunning || !syncDirty) {
      if (!syncRunning && !syncDirty) flushIdleWaiters();
      return;
    }
    syncRunning = true;
    syncDirty = false;
    var data = pendingCartData;
    pendingCartData = null;
    kickTicker();
    updateBusyUI();

    var pass;
    try {
      pass = reconcile(data);
    } catch (e) {
      pass = Promise.resolve(false);
    }

    Promise.resolve(pass)
      .catch(function (e) {
        console.error('LogOX rewards: sync failed', e);
        return false;
      })
      .then(function (ok) {
        syncRunning = false;
        if (ok) {
          syncFailures = 0;
          lastVerifiedAt = Date.now();
        } else if (syncFailures < 1) {
          // one retry, then fail open — a broken network must never leave the
          // customer permanently unable to check out
          syncFailures++;
          syncDirty = true;
          updateBusyUI();
          kickTicker();
          setTimeout(pumpSync, 400);
          return;
        } else {
          syncFailures = 0;
          lastVerifiedAt = Date.now();
          console.warn('LogOX rewards: giving up on cart reconcile');
        }
        updateBusyUI();
        kickTicker();
        if (syncDirty) pumpSync();
        else flushIdleWaiters();
      });
  }

  // public entry point — kept for cart.js / product-form.js.
  // Resolves once the cart is reconciled (not just once the request is sent).
  function sync(cartData) {
    requestSync(cartData);
    return whenIdle();
  }

  /* ---- upsells / protection / discount (event delegation) -------- */

  function addVariant(variantId, quantity) {
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: quantity || 1 }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    }).then(function (res) {
      if (!res.ok) {
        console.warn('LogOX rewards: add to cart failed', res.status);
        return null;
      }
      return res.json();
    });
  }

  document.addEventListener('click', function (event) {
    var addBtn = event.target.closest('[data-lx-upsell-add]');
    if (addBtn) {
      event.preventDefault();
      if (addBtn.disabled) return;
      addBtn.disabled = true;
      addVariant(addBtn.getAttribute('data-lx-upsell-add'), 1)
        .then(function (data) {
          if (data && data.sections) rerenderFromSections(data.sections);
          openDrawer();
          return sync();
        })
        .finally(function () {
          addBtn.disabled = false;
        });
      return;
    }

    // upsell slider arrows
    var upArrow = event.target.closest('[data-lx-up-prev], [data-lx-up-next]');
    if (upArrow) {
      event.preventDefault();
      var track = document.querySelector('[data-lx-up-scroll]');
      if (!track) return;
      var isNext = upArrow.hasAttribute('data-lx-up-next');
      var card = track.querySelector('.lx-uc');
      var step = card ? (card.offsetWidth + 10) * 2 : track.clientWidth * 0.8;
      track.scrollBy({ left: isNext ? step : -step, behavior: 'smooth' });
      setTimeout(initUpsellSlider, 400);
      return;
    }

    // (?) info popup — sits inside the protection <label>, so stop the click
    // from toggling the checkbox
    var infoBtn = event.target.closest('[data-lx-protect-info]');
    if (infoBtn) {
      event.preventDefault();
      event.stopPropagation();
      var pop = infoBtn.closest('.lx-insure') && infoBtn.closest('.lx-insure').querySelector('[data-lx-protect-popup]');
      if (pop) {
        var willShow = pop.hasAttribute('hidden');
        if (willShow) pop.removeAttribute('hidden');
        else pop.setAttribute('hidden', '');
        infoBtn.setAttribute('aria-expanded', willShow ? 'true' : 'false');
      }
      return;
    }

    // click anywhere else closes an open protection popup
    var openPop = document.querySelector('[data-lx-protect-popup]:not([hidden])');
    if (openPop && !event.target.closest('[data-lx-protect-popup]')) {
      openPop.setAttribute('hidden', '');
      var btn = document.querySelector('[data-lx-protect-info]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    }
  });

  document.addEventListener('change', function (event) {
    var protect = event.target.closest('[data-lx-protect]');
    if (!protect) return;
    var cfg = getConfig();
    var variantId = protect.getAttribute('data-variant-id');
    var label = protect.closest('.lx-insure');
    if (label) label.classList.toggle('on', protect.checked);
    protect.disabled = true;

    var op;
    if (protect.checked) {
      if (!variantId) { protect.disabled = false; return; }
      op = addVariant(variantId, 1);
    } else {
      // remove every protection tier variant (only the one in cart actually changes)
      var updates = {};
      ((cfg && cfg.protectTiers) || []).forEach(function (t) { updates[t.variantId] = 0; });
      if (variantId) updates[variantId] = 0;
      op = applyUpdates(updates);
    }

    op.then(function (data) {
      if (data && data.sections) rerenderFromSections(data.sections);
      else return refreshDrawer();
    }).finally(function () {
      var fresh = document.querySelector('[data-lx-protect]');
      if (fresh) fresh.disabled = false;
    });
  });

  // keep slider arrows in sync when the customer swipes the track by hand
  document.addEventListener('scroll', function (event) {
    var t = event.target;
    if (t && t.nodeType === 1 && t.matches && t.matches('[data-lx-up-scroll]')) initUpsellSlider();
  }, true);

  /* ---- GovX app widget preservation ------------------------------- */
  // The GovX app injects its banner into [data-govx-id] once on page load.
  // Every drawer re-render swaps .drawer__inner innerHTML, wiping the injected
  // widget (the app does not re-inject). Snapshot the injected markup whenever
  // it's present and restore it after re-renders.
  var govxSnapshot = null;
  var drawerScheduled = false;

  function govxSync() {
    var slot = document.querySelector('[data-lx-govx-slot]');
    if (!slot) return;
    var hasWidget = !!slot.querySelector('iframe, a, img, button, div, span');
    if (hasWidget) {
      govxSnapshot = slot.innerHTML;
    } else if (govxSnapshot) {
      slot.innerHTML = govxSnapshot;
    }
  }

  // runs (debounced) after any drawer DOM change — covers every render path
  // (our rerenderFromSections, Dawn's renderContents, cart.js onCartUpdate)
  function onDrawerChanged() {
    drawerScheduled = false;
    govxSync();
    loadAutoRecommendations();
    initUpsellSlider();
    // the footer (and its checkout button) is re-rendered on every cart update,
    // so re-apply the busy state and make sure the loader node is still there
    ensureLoader();
    updateBusyUI();
  }

  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof MutationObserver === 'undefined') return;
    new MutationObserver(function () {
      if (drawerScheduled) return;
      drawerScheduled = true;
      requestAnimationFrame(onDrawerChanged);
    }).observe(drawer, { childList: true, subtree: true });
    onDrawerChanged();
  }

  /* ---- wiring ----------------------------------------------------- */

  // Re-check gifts whenever any Dawn component announces a cart change.
  function subscribeWhenReady() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'lx-rewards') return;
        // add.js responses aren't full carts — always re-fetch inside sync()
        sync();
      });
      return true;
    }
    return false;
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (!subscribeWhenReady()) setTimeout(subscribeWhenReady, 1000);
    watchDrawer();
    // if a gift line rendered as a normal item (stale cached HTML on load),
    // pull a fresh drawer render so it shows as a FREE GIFT card immediately
    if (giftMisclassified()) refreshDrawer();
    // reconcile stale carts (e.g. thresholds changed since the cart was built)
    sync();
  });

  window.LogoxCartRewards = {
    sync: sync,
    refreshDrawer: refreshDrawer,
    // exposed for debugging / any other script that needs to wait on the cart
    isBusy: isCartBusy,
    whenSettled: function () {
      return waitForSettled(HOLD_MAX_MS);
    },
  };
})();
